// Renvois : quand un bloc se pose, les mots qui le désignent ailleurs dans la page se soulignent
// avec lui, le temps de faire le lien (la preuve des studios et l'emploi chez Mecano). Lit :
//
//   data-renvoi="<nom>"              un élément qui contient le mot du renvoi
//   data-renvoi-mot="<mot>"          ce mot, tel qu'il s'écrit dans le texte
//   data-renvoi-declencheur="<nom>"  le bloc dont la pose joue le renvoi (un effet qui la
//                                    signale : filet, tiroir)
//   --renvois: non                   sur la racine, là où la mise en page sépare les mots d'un
//                                    renvoi (une colonne : le titre du chapitre loin au-dessus
//                                    de l'emploi) ; mouvement.scss la pose
//
// Le texte n'est pas touché : chaque trait est un calque posé sous le mot, dans sa section, puis
// retiré. En mouvement réduit, rien.

import { gsap } from 'gsap';

import { calque, quandPose, retirer } from './outils.ts';
import { COURBE } from './reglages.ts';

/** Les rectangles d'un mot dans un élément, un par ligne qu'il occupe. */
function rectsMot(el: Element, mot: string): DOMRect[] {
  const noeuds: Text[] = [];
  const marcheur = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  while (marcheur.nextNode())
    if (marcheur.currentNode instanceof Text) noeuds.push(marcheur.currentNode);
  const debut = noeuds
    .map((n) => n.data)
    .join('')
    .indexOf(mot);
  if (debut < 0) return [];
  const fin = debut + mot.length;
  const plage = document.createRange();
  let pos = 0;
  for (const n of noeuds) {
    const apres = pos + n.data.length;
    if (debut >= pos && debut < apres) plage.setStart(n, debut - pos);
    if (fin > pos && fin <= apres) {
      plage.setEnd(n, fin - pos);
      break;
    }
    pos = apres;
  }
  // Les fragments d'une même ligne (un par nœud de texte) se fondent en un seul.
  const lignes: DOMRect[] = [];
  for (const r of plage.getClientRects()) {
    if (r.width < 1) continue;
    const l = lignes.find((x) => Math.abs(x.bottom - r.bottom) < 2);
    if (!l) lignes.push(DOMRect.fromRect(r));
    else {
      const g = Math.min(l.left, r.left);
      l.width = Math.max(l.right, r.right) - g;
      l.x = g;
    }
  }
  return lignes;
}

/**
 * Les traits d'un renvoi, posés sous chaque mot, dans la section qui le porte. Le lien se fait à
 * l'œil : il faut ses mots côte à côte (pas sous --renvois: non) et au moins deux d'entre eux à
 * l'écran, sinon aucun trait.
 */
function traits(nom: string): HTMLElement[] {
  const racine = getComputedStyle(document.documentElement);
  if (racine.getPropertyValue('--renvois').trim() === 'non') return [];
  const vus = [...document.querySelectorAll<HTMLElement>(`[data-renvoi="${nom}"]`)].filter((el) =>
    rectsMot(el, el.dataset.renvoiMot ?? '').some((r) => r.bottom > 0 && r.top < innerHeight),
  );
  if (vus.length < 2) return [];
  const poses: HTMLElement[] = [];
  for (const el of vus) {
    // La section colle, donc est positionnée ; elle peut reculer (son échelle, k).
    const hote = el.closest<HTMLElement>('[data-nav]');
    const mot = el.dataset.renvoiMot ?? '';
    if (!hote || !mot) continue;
    const hb = hote.getBoundingClientRect();
    const k = hb.width / hote.offsetWidth || 1;
    const style = getComputedStyle(el);
    const taille = parseFloat(style.fontSize) || 16;
    // Le trait se cale sur la ligne de base : le bas de la boîte du texte (sa descente) passe sous
    // la ligne suivante quand l'interligne est serré.
    const mesure = document.createElement('canvas').getContext('2d');
    if (mesure) mesure.font = style.font;
    const descente = mesure?.measureText(mot).fontBoundingBoxDescent ?? 0;
    for (const r of rectsMot(el, mot)) {
      const trait = calque('i', 'renvoi-trait', hote);
      Object.assign(trait.style, {
        left: `${String((r.left - hb.left) / k - hote.clientLeft)}px`,
        top: `${String((r.bottom - descente - hb.top) / k - hote.clientTop + taille * 0.06)}px`,
        width: `${String(r.width / k)}px`,
        height: `${String(Math.max(2, Math.round(taille * 0.07)))}px`,
        background: style.color,
      });
      poses.push(trait);
    }
  }
  return poses;
}

/** Branche chaque renvoi sur la pose de son déclencheur. */
export function monterRenvois(): void {
  for (const d of document.querySelectorAll<HTMLElement>('[data-renvoi-declencheur]')) {
    const nom = d.dataset.renvoiDeclencheur ?? '';
    quandPose(d, () => {
      const t = traits(nom);
      if (!t.length) return;
      gsap
        .timeline({
          delay: 0.1,
          onComplete: () => {
            for (const tr of t) retirer(tr);
          },
        })
        .fromTo(
          t,
          { scaleX: 0, transformOrigin: '0% 50%' },
          { scaleX: 1, duration: 0.6, ease: COURBE.arrivee, stagger: 0.1 },
        )
        .set(t, { transformOrigin: '100% 50%' }, '+=1.2')
        .to(t, { scaleX: 0, duration: 0.45, ease: 'power2.in' });
    });
  }
}
